import { message } from 'antd';
import InputPanel from './InputPanel';
import ResultsPanel from './ResultsPanel';
import ArcDiagram from './ArcDiagram';
import HelpSection from './HelpSection';
import { buildClipboardSummary } from './clipboard';
import type { SagittaResult, UnitSystem } from './types';

export interface WidgetContentProps {
	unit: UnitSystem;
	onUnitChange: (u: UnitSystem) => void;
	inputMode: 'radius' | 'diameter';
	onInputModeChange: (m: 'radius' | 'diameter') => void;
	chordStr: string;
	onChordStrChange: (s: string) => void;
	onChordFocus: () => void;
	onChordBlur: () => void;
	radiusOrDiameterStr: string;
	onRadiusOrDiameterStrChange: (s: string) => void;
	onRadiusDiaFocus: () => void;
	onRadiusDiaBlur: () => void;
	tolPlusStr: string;
	onTolPlusStrChange: (s: string) => void;
	tolMinusStr: string;
	onTolMinusStrChange: (s: string) => void;
	chordError: string | null;
	radiusError: string | null;
	tolPlusError: string | null;
	tolMinusError: string | null;
	geometryError: string | null;
	toleranceError: string | null;
	result: SagittaResult | null;
	placeholder: string;
	displayRadius: number | null;
	displayChord: number | null;
	/** Raw radius or diameter value as entered, for copy summary. */
	radiusOrDiameterField: number | null;
	onReset: () => void;
}

export default function WidgetContent(props: WidgetContentProps) {
	const {
		unit,
		onUnitChange,
		inputMode,
		onInputModeChange,
		chordStr,
		onChordStrChange,
		onChordFocus,
		onChordBlur,
		radiusOrDiameterStr,
		onRadiusOrDiameterStrChange,
		onRadiusDiaFocus,
		onRadiusDiaBlur,
		tolPlusStr,
		onTolPlusStrChange,
		tolMinusStr,
		onTolMinusStrChange,
		chordError,
		radiusError,
		tolPlusError,
		tolMinusError,
		geometryError,
		toleranceError,
		result,
		placeholder,
		displayRadius,
		displayChord,
		radiusOrDiameterField,
		onReset,
	} = props;

	const canCopy =
		!!result &&
		!Number.isNaN(result.nominal) &&
		displayChord != null &&
		radiusOrDiameterField != null;

	const hasAnyInput = !!(
		chordStr ||
		radiusOrDiameterStr ||
		tolPlusStr ||
		tolMinusStr
	);

	async function onCopyAll() {
		if (!canCopy) return;
		const text = buildClipboardSummary(
			unit,
			result!,
			displayChord!,
			radiusOrDiameterField!,
			inputMode
		);
		try {
			await navigator.clipboard.writeText(text);
			message.success('Copied');
		} catch {
			message.error('Copy failed');
		}
	}

	return (
		<div className="space-y-4 max-h-[80vh] overflow-y-auto pr-1">
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
				<div>
					<InputPanel
						unit={unit}
						onUnitChange={onUnitChange}
						inputMode={inputMode}
						onInputModeChange={onInputModeChange}
						chordStr={chordStr}
						onChordStrChange={onChordStrChange}
						onChordFocus={onChordFocus}
						onChordBlur={onChordBlur}
						radiusOrDiameterStr={radiusOrDiameterStr}
						onRadiusOrDiameterStrChange={onRadiusOrDiameterStrChange}
						onRadiusDiaFocus={onRadiusDiaFocus}
						onRadiusDiaBlur={onRadiusDiaBlur}
						tolPlusStr={tolPlusStr}
						onTolPlusStrChange={onTolPlusStrChange}
						tolMinusStr={tolMinusStr}
						onTolMinusStrChange={onTolMinusStrChange}
						chordError={chordError}
						radiusError={radiusError}
						tolPlusError={tolPlusError}
						tolMinusError={tolMinusError}
						geometryError={geometryError}
						toleranceError={toleranceError}
						onReset={onReset}
						canReset={hasAnyInput}
					/>
				</div>
				<div className="lg:sticky lg:top-0">
					<ArcDiagram
						chord={displayChord}
						radius={displayRadius}
						unit={unit}
					/>
				</div>
			</div>
			<ResultsPanel
				unit={unit}
				result={result}
				placeholder={placeholder}
				onCopyAll={onCopyAll}
				canCopy={canCopy}
			/>
			<HelpSection />
		</div>
	);
}
